import React, { useState } from 'react';
import { UserPlus, Mail, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useStore } from '../../lib/store';
import { getTenantSlugFromHostname } from '../../lib/tenant';

export default function UserInvite() {
  const { user } = useStore();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'staff' as 'owner' | 'admin' | 'staff' | 'viewer',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const tenantSlug = getTenantSlugFromHostname();
      if (!tenantSlug) {
        throw new Error('Tenant not found. Please access this page from your tenant subdomain.');
      }

      const adminApiUrl = import.meta.env.VITE_ADMIN_API_URL || 'https://api.medsystem.app';
      const response = await fetch(`${adminApiUrl}/api/tenants/by-slug/${tenantSlug}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to send invite');
      }

      toast.success('Invite sent successfully!');
      setFormData({ email: '', name: '', role: 'staff' });
    } catch (error: any) {
      toast.error(error.message || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <UserPlus className="w-6 h-6 mr-2 text-teal-600" />
          Invite User
        </h2>
        <p className="text-gray-600 mt-2">
          Send an invitation email to add a new user to your hospital system
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="user@example.com"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              An invitation email will be sent to this address
            </p>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Dr. John Doe"
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
              Role *
            </label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
              <option value="viewer">Viewer</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              <strong>Admin:</strong> Full access to manage users and settings<br />
              <strong>Staff:</strong> Standard access to hospital operations<br />
              <strong>Viewer:</strong> Read-only access to reports and data
            </p>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => setFormData({ email: '', name: '', role: 'staff' })}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">How it works</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• The user will receive an email with an invitation link</li>
          <li>• They can click the link to set up their account</li>
          <li>• Once accepted, they'll have access based on their assigned role</li>
          <li>• You can manage their permissions from the User Management page</li>
        </ul>
      </div>
    </div>
  );
}
